// import React from 'react';
// import Widget from '../Widget';

// export default function Appearance() {
//   return <Widget />;
// }


import React, { useState, useEffect } from 'react';
// This file is now used inside layered tabs; keep exports unchanged for backward compatibility
import Layout from '../../../Components/Layout';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QuestionAnswer as QuestionIcon,
  Refresh as RefreshIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getSuggestedQuestions, createSuggestedQuestion, updateSuggestedQuestion, deleteSuggestedQuestion, getWidgetColor, saveWidgetColor, saveTermsAndConditions, saveReturnEmail } from '../../../redux/actions/widgetActions';
// import LinearLoading from '../Components/reuse/LinearLoading';
import useCallOnce from '../../../hooks/useCallOnce';

function Appearance() {
  const dispatch = useDispatch();
  const { 
    suggestedQuestions, 
    loading, 
    creating, 
    pagination,
    widgetColor: reduxWidgetColor,
    widgetColorLoading,
    savingTerms
  } = useSelector((state) => state.widget);
  const user = useSelector((state) => state?.auth?.user);



  // Local state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState('product');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deletingQuestion, setDeletingQuestion] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Color selection state
  const [widgetColor, setWidgetColor] = useState(reduxWidgetColor || '#000000');
  
  // Terms and conditions state
  const [termCondition, setTermCondition] = useState(user?.termCondition || '');
  
  // Return email state
  const [returnEmail, setReturnEmail] = useState(user?.returnEmail || '');
  const [emailError, setEmailError] = useState('');

  // Sync local widget color with Redux state
  useEffect(() => {
    if (reduxWidgetColor && reduxWidgetColor !== widgetColor) {
      // If we have a color from API and it's different from local, use it
      setWidgetColor(reduxWidgetColor);
    }
  }, [reduxWidgetColor]); // Only depend on reduxWidgetColor to prevent loops

  // Initialize color on mount
  useEffect(() => {
    if (reduxWidgetColor && !widgetColor) {
      setWidgetColor(reduxWidgetColor);
    }
  }, []); // Empty dependency array - runs only once on mount

  // Sync termCondition with user data
  useEffect(() => {
    if (user?.termCondition !== undefined) {
      setTermCondition(user.termCondition);
    }
  }, [user?.termCondition]);

  // Sync returnEmail with user data
  useEffect(() => {
    if (user?.returnEmail !== undefined) {
      setReturnEmail(user.returnEmail);
      setEmailError(''); // Clear any existing error when syncing with user data
    }
  }, [user?.returnEmail]);

  // Load suggested questions and widget color on component mount using useCallOnce
  useCallOnce('widget/getSuggestedQuestions', () => {
    dispatch(getSuggestedQuestions(1, rowsPerPage));
  });

  useCallOnce('widget/getWidgetColor', () => {
    dispatch(getWidgetColor());
  });

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(getSuggestedQuestions(newPage + 1, rowsPerPage));
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    dispatch(getSuggestedQuestions(1, newRowsPerPage));
  };

  // Handle add question dialog open
  const handleAddDialogOpen = () => {
    setAddDialogOpen(true);
    setQuestionText('');
  };

  // Handle add question dialog close
  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
    setQuestionText('');
    setQuestionType('product');
  };

  // Handle question submission
  const handleSubmitQuestion = async () => {
    if (!questionText.trim()) return;

    const payload = {
      question: questionText.trim(),
      type: questionType
    };

    await dispatch(createSuggestedQuestion(payload, refreshQuestions));
    handleAddDialogClose();
  };

  // Refresh questions list
  const refreshQuestions = () => {
    dispatch(getSuggestedQuestions(page + 1, rowsPerPage));
  };

  // Handle refresh button click
  const handleRefresh = () => {
    // Reset pagination to first page
    setPage(0);
    // Reset rows per page to default
    setRowsPerPage(10);
    // Refresh data from first page with loading state
    dispatch(getSuggestedQuestions(1, 10));
  };

  // Handle edit click
  const handleEditClick = (question) => {
    setEditingQuestion(question);
    setQuestionText(question.question);
    setQuestionType(question.type || 'product');
    setEditDialogOpen(true);
  };

  // Handle edit dialog close
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingQuestion(null);
    setQuestionText('');
    setQuestionType('product');
  };

  // Handle edit submission
  const handleEditSubmit = async () => {
    if (!questionText.trim() || !editingQuestion) return;

    const payload = {
      question: questionText.trim(),
      type: questionType
    };

    await dispatch(updateSuggestedQuestion(editingQuestion._id, payload, refreshQuestions));
    handleEditDialogClose();
  };

  // Handle delete click
  const handleDeleteClick = (question) => {
    setDeletingQuestion(question);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingQuestion) return;

    await dispatch(deleteSuggestedQuestion(deletingQuestion._id, refreshQuestions));
    setDeleteDialogOpen(false);
    setDeletingQuestion(null);
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDeletingQuestion(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get type color for chip
  const getTypeColor = (type) => {
    switch (type) {
      case 'order':
        return 'primary';
      case 'product':
        return 'success';
      case 'support':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Handle color change
  const handleColorChange = (event) => {
    const newColor = event.target.value;
    setWidgetColor(newColor);
  };

  // Handle save color
  const handleSaveColor = () => {
    dispatch(saveWidgetColor(widgetColor));
  };

  // Handle copy color to clipboard
  const handleCopyColor = async () => {
    try {
      await navigator.clipboard.writeText(widgetColor);
      // You can add a toast notification here if you want
    } catch (err) {
      console.error('Failed to copy color:', err);
    }
  };

  // Handle save terms and conditions
  const handleSaveTerms = () => {
    dispatch(saveTermsAndConditions(termCondition));
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setReturnEmail(value);
    
    if (value === '') {
      setEmailError('');
    } else if (!validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  // Check if email is valid and not empty
  const isEmailValid = returnEmail.trim() !== '' && validateEmail(returnEmail) && !emailError;

  // Handle save return email
  const handleSaveReturnEmail = () => {
    if (isEmailValid) {
      dispatch(saveReturnEmail(returnEmail));
    }
  };

  return (
      <Box>
        {/* Color Selection Box */}
         <Paper sx={{ 
           width: '100%', 
           mb: 3, 
           p: 3,
           borderRadius: 2,
           boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
         }}>
           {/* Widget Color Heading */}
           <Typography variant="body2" sx={{ 
             //  color: '#6b7280', 
              mb: 2, 
              fontSize: '16px',
              fontWeight: 600 
            }}>
              Widget Color
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {/* Color Preview */}
              <Box 
                key={widgetColor} // Force re-render when color changes
                sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%',
                  background: widgetColor ? `radial-gradient(circle, ${widgetColor} 0%, ${widgetColor}dd 50%, ${widgetColor}aa 100%)` : '#000000',
                  border: '2px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                {widgetColorLoading ? (
                  <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                ) : (
                  <PaletteIcon sx={{ fontSize: 24, color: '#ffffff' }} />
                )}
              </Box>
            
            {/* Color Input and Controls */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Color Swatch - Clickable */}
                  {/* <Box 
                    key={`swatch-${widgetColor}`} // Force re-render when color changes
                    onClick={() => document.getElementById('color-picker-input').click()}
                    sx={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%', 
                      backgroundColor: widgetColor,
                      border: '2px solid #ffffff',
                      boxShadow: '0 0 0 2px #e5e7eb',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 0 0 3px #9ca3af'
                      }
                    }} 
                  /> */}
                  
                  {/* HEX Color Input Field with Copy Button */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                   <input
                     id="color-picker-input"
                     type="color"
                     value={widgetColor}
                     onChange={handleColorChange}
                     style={{
                       width: 40,
                       height: 40,
                       boxShadow: 'none',
                       border: '1px solid #fff',
                       // borderRadius: '8px',
                       cursor: 'pointer',
                       outline: 'none',
                       padding: 0,
                       marginRight: "8px"
                     }}
                   />
                    
                                        <TextField
                                        // disabled={true}
                                        readOnly={true}
                       size="small"
                       value={widgetColor || ''}
                                              onChange={(e) => {
                          const value = e.target.value;
                          if (value.match(/^#[0-9A-Fa-f]{6}$/)) {
                            setWidgetColor(value);
                          }
                        }}
                       placeholder="#000000"
                       sx={{
                         width: 120,
                         '& .MuiOutlinedInput-root': {
                           fontSize: '14px',
                           fontFamily: 'monospace'
                         }
                       }}
                       inputProps={{
                         style: { 
                           textAlign: 'center',
                           fontFamily: 'monospace',
                           fontWeight: 500
                         }
                       }}
                     />
                    {/* <IconButton
                      size="small"
                      onClick={handleCopyColor}
                      sx={{
                        ml: 1,
                        color: '#6b7280',
                        '&:hover': {
                          color: '#374151',
                          backgroundColor: '#f3f4f6'
                        }
                      }}
                      title="Copy color code"
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton> */}
                  </Box>
                  
                                    {/* Native Color Picker */}
                   
                  
                  {/* Save Button */}
                  <Button
                    variant="contained"
                    onClick={handleSaveColor}
                    disabled={widgetColorLoading}
                    sx={{
                      backgroundColor: '#374151',
                      color: '#ffffff',
                      borderRadius: '8px',
                      px: 3,
                      py: 0.5,
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#1f2937'
                      },
                      '&:disabled': {
                        backgroundColor: '#9ca3af',
                        cursor: 'not-allowed'
                      }
                    }}
                  >
                    {widgetColorLoading ? 'Loading...' : 'Save'}
                  </Button>
                </Box>
             </Box>
           </Box>
         </Paper>

        {/* Return Email Section */}
         {/* <Paper sx={{ 
           width: '100%', 
           mb: 3, 
           p: 3,
           borderRadius: 2,
           boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
         }}>
           <Typography variant="body2" sx={{ 
             fontSize: '16px',
             fontWeight: 600 
           }}>
             Return Email
           </Typography>
           
           <Typography variant="body2" sx={{ 
             color: '#6b7280', 
             mb: 2, 
             fontSize: '14px'
           }}>
             Set the email address for return related queries and communications.
           </Typography>
           
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
             <TextField
               fullWidth
               label="Return Email"
               type="email"
               value={returnEmail}
               onChange={handleEmailChange}
               placeholder="Enter return email address"
               variant="outlined"
               size="small"
               error={!!emailError}
               helperText={emailError}
               sx={{
                 '& .MuiOutlinedInput-root': {
                   fontSize: '14px'
                 }
               }}
             />
             
             <Button
               variant="contained"
               onClick={handleSaveReturnEmail}
               disabled={savingTerms || !isEmailValid}
               sx={{
                 backgroundColor: '#374151',
                 color: '#ffffff',
                 borderRadius: '8px',
                 px: 3,
                 py: 0.5,
                 textTransform: 'none',
                 fontSize: '14px',
                 fontWeight: 600,
                 '&:hover': {
                   backgroundColor: isEmailValid ? '#1f2937' : '#9ca3af'
                 },
                 '&:disabled': {
                   backgroundColor: '#9ca3af',
                   borderColor: '#9ca3af',
                   cursor: 'not-allowed'
                 }
               }}
             >
               {savingTerms ? 'Saving...' : 'Save'}
             </Button>
           </Box>
         </Paper> */}

        {/* Terms and Conditions Section */}
         <Paper sx={{ 
           width: '100%', 
           mb: 3, 
           p: 3,
           borderRadius: 2,
           boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
         }}>
           {/* Terms and Conditions Heading */}
           <Typography variant="body2" sx={{ 
             fontSize: '16px',
             fontWeight: 600 
           }}>
             Terms and conditions
           </Typography>
           
           <Typography variant="body2" sx={{ 
             color: '#6b7280', 
             mb: 2, 
             fontSize: '14px'
           }}>
             Require the caller to accept your terms and conditions before initiating a call.
           </Typography>
           
           {/* Terms Content Box */}
           <Box sx={{ 
             backgroundColor: '#ffffff',
             border: '1px solid #e5e7eb',
             borderRadius: '8px',
            //  p: 2,
             mb: 2
           }}>
             {/* <Typography variant="body2" sx={{ 
               mb: 1, 
               fontSize: '14px',
               fontWeight: 500,
               color: '#374151'
             }}>
               Terms content
             </Typography> */}
             
             <TextField
               fullWidth
               multiline
               rows={3}
               value={termCondition}
               onChange={(e) => setTermCondition(e.target.value)}
               placeholder='Example:
#### Terms and conditions
By clicking &quot;Accept,&quot; you agree to our [terms](www.example.com/terms).'
               variant="outlined"
               sx={{
                 '& .MuiOutlinedInput-root': {
                   fontSize: '14px',
                   '& fieldset': {
                     border: 'none'
                   }
                 }
               }}
             />
           </Box>
           
           {/* Save Button */}
           <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
             <Button
               variant="contained"
               onClick={handleSaveTerms}
               disabled={savingTerms || !termCondition.trim()}
               sx={{
                 backgroundColor: '#374151',
                 color: '#ffffff',
                 borderRadius: '8px',
                 px: 3,
                 py: 0.5,
                 textTransform: 'none',
                 fontSize: '14px',
                 fontWeight: 600,
                 '&:hover': {
                   backgroundColor: termCondition.trim() ? '#1f2937' : '#9ca3af'
                 },
                 '&:disabled': {
                   backgroundColor: '#9ca3af',
                   borderColor: '#9ca3af',
                   cursor: 'not-allowed'
                 }
               }}
             >
               {savingTerms ? 'Saving...' : 'Save'}
             </Button>
           </Box>
         </Paper>

        {/* Action Buttons Section - Above Table */}
         <Box sx={{ 
           display: 'flex', 
           justifyContent: 'space-between', 
           alignItems: 'center',
           mb: 3,
           flexDirection: { xs: 'column', sm: 'row' },
           gap: { xs: 2, sm: 0 }
         }}>
           {/* Left Side - Subheading */}
           <Box sx={{ flex: 1 }}>
            
             <Typography variant="h6" sx={{ 
               fontWeight: 600,
               fontSize: { xs: '14px', sm: '16px' },
               color: '#374151'
             }}>
               Manage suggested questions for your AI widget
             </Typography>
           </Box>

           {/* Right Side - Action Buttons */}
           <Box sx={{ 
             display: 'flex', 
             gap: 2, 
             flexDirection: { xs: 'column', sm: 'row' },
             flexShrink: 0,
             width: { xs: '100%', sm: 'auto' }
           }}>
             <Button
               variant="outlined"
               startIcon={<RefreshIcon />}
               onClick={handleRefresh}
               disabled={loading}
               sx={{
                 backgroundColor: '#ffffff',
                 borderColor: '#d1d5db',
                 color: '#374151',
                 borderRadius: '25px',
                //  px: 2,
                //  py: 1.5,
                 textTransform: 'none',
                 fontSize: '14px',
                 fontWeight: 500,
                 width: { xs: '100%', sm: 'auto' },
                 '&:hover': { 
                   backgroundColor: '#f9fafb',
                   borderColor: '#9ca3af'
                 },
                 '&:disabled': {
                   backgroundColor: '#f9fafb',
                   borderColor: '#e5e7eb',
                   color: '#9ca3af'
                 }
               }}
             >
               Refresh
             </Button>
             <Button
               variant="contained"
               startIcon={<AddIcon />}
               onClick={handleAddDialogOpen}
               sx={{
                 backgroundColor: '#374151',
                 color: '#ffffff',
                 borderRadius: '25px',
                //  px: 3,
                //  py: 1.5,
                 textTransform: 'none',
                 fontSize: '14px',
                 fontWeight: 600,
                 border: '1px solid #000000',
                 boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                 width: { xs: '100%', sm: 'auto' },
                 '&:hover': { 
                   backgroundColor: '#1f2937',
                   boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                 },
                 '&:active': {
                   backgroundColor: '#111827',
                   transform: 'translateY(1px)'
                 }
               }}
             >
               Add Question
             </Button>
           </Box>
         </Box>

        {/* Questions Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Question</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Created At</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  // Show centered loading spinner when refreshing
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 8 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="body1" sx={{ color: '#6b7280' }}>
                          Loading questions...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : suggestedQuestions.length > 0 ? (
                  // Show actual data when loaded
                  suggestedQuestions.map((question, index) => (
                    <TableRow key={question._id || index} hover>
                      <TableCell sx={{ maxWidth: 400 }}>
                        <Typography variant="body2" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {question.question}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={question.type || 'N/A'}
                          color={getTypeColor(question.type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {formatDate(question.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditClick(question)}
                          // sx={{ color: '#3b82f6' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteClick(question)}
                          sx={{ color: '#ef4444' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  // Show empty state when no data
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <QuestionIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                        <Typography variant="body1" sx={{ color: '#6b7280', mb: 1 }}>
                          No suggested questions found
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                          Start by adding your first suggested question
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={pagination.totalCount || pagination.totalItems || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid #e5e7eb' }}
          />
        </Paper>

        {/* Add Question Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleAddDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              bgcolor: '#222',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center'
            }}>
              <QuestionIcon sx={{ fontSize: 20, color: '#ffffff' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add Suggested Question
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 3, pb: 2 }}>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel id="question-type-label">Question Type</InputLabel>
              <Select
                labelId="question-type-label"
                value={questionType}
                label="Question Type"
                onChange={(e) => setQuestionType(e.target.value)}
              >
                <MenuItem value="product">Product</MenuItem>
                <MenuItem value="order">Order</MenuItem>
               
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Question"
              multiline
              rows={3}
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="Enter your suggested question here..."
              variant="outlined"
              
            />
            
            
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb' }}>
            <Button 
              onClick={handleAddDialogClose} 
              color="inherit"
              disabled={creating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitQuestion} 
              variant="contained"
              disabled={!questionText.trim() || creating}
              startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
              // sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
            >
              {creating ? 'Adding...' : 'Add Question'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Question Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              bgcolor: '#222',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center'
            }}>
              <EditIcon sx={{ fontSize: 20, color: '#ffffff' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Edit Suggested Question
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 3, pb: 2 }}>
          <FormControl fullWidth  sx={{ mt: 1, mb: 2 }}>
              <InputLabel id="question-type-label">Question Type</InputLabel>
              <Select
                labelId="question-type-label"
                value={questionType}
                label="Question Type"
                onChange={(e) => setQuestionType(e.target.value)}
              >
                <MenuItem value="product">Product</MenuItem>
                <MenuItem value="order">Order</MenuItem>
                
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Question"
              multiline
              rows={3}
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="Enter your suggested question here..."
              variant="outlined"
             
            />
            
            
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb' }}>
            <Button 
              onClick={handleEditDialogClose} 
              color="inherit"
              disabled={creating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEditSubmit} 
              variant="contained"
              disabled={!questionText.trim() || creating}
              startIcon={creating ? <CircularProgress size={16} /> : <EditIcon />}
              // sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
            >
              {creating ? 'Editing...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Question Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            borderBottom: '1px solid #e5e7eb',
            pb: 2
          }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              bgcolor: '#ef4444',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center'
            }}>
              <DeleteIcon sx={{ fontSize: 20, color: '#ffffff' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Confirm Deletion
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Typography variant="body1" sx={{ color: '#333', mb: 2 }}>
              Are you sure you want to delete this suggested question? This action cannot be undone.
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Question: {deletingQuestion?.question}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Type: {deletingQuestion?.type || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Created At: {formatDate(deletingQuestion?.createdAt)}
            </Typography>
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb' }}>
            <Button 
              onClick={handleDeleteDialogClose} 
              color="inherit"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              variant="contained"
              color="error"
              disabled={creating}
              startIcon={creating ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {creating ? 'Deleting...' : 'Delete Question'}
            </Button>
          </DialogActions>
        </Dialog>


      </Box>
  );
}

export default Appearance;

